(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-model-comparison]');
  const packs = new Set(['single', 'double']);
  const clean = value => typeof value === 'string' ? value.trim() : '';
  const strings = value => Array.isArray(value) ? value.map(clean).filter(Boolean) : [];
  const normalized = value => clean(value).replace(/\s+/g, ' ').toLocaleLowerCase('en');

  function safeLink(value) {
    if (!clean(value)) return null;
    try {
      const url = new URL(value, location.href);
      return ['http:', 'https:'].includes(url.protocol) && url.origin === location.origin ? url : null;
    } catch { return null; }
  }

  function safeImage(value) {
    if (!clean(value)) return '';
    try {
      const url = new URL(value, location.href);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch { return ''; }
  }

  function element(tag, text, className) {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  }

  roots.forEach((root, rootIndex) => {
    if (root.dataset.comparisonInitialized === 'true') return;
    const dataElement = root.querySelector('script[data-comparison-data]');
    const table = root.querySelector('table[data-compare-table]');
    const selects = [0, 1, 2].map(index => root.querySelector(`[data-compare-slot="${index}"]`));
    if (!dataElement || !table || !selects[0] || !selects[1]) return;

    let data;
    try { data = JSON.parse(dataElement.textContent || '{}'); } catch { return; }
    if (!Array.isArray(data.products) || !Array.isArray(data.rows)) return;
    const products = [];
    const byHandle = new Map();
    for (const raw of data.products) {
      if (!raw || typeof raw !== 'object' || !clean(raw.handle) || byHandle.has(raw.handle)) continue;
      const product = {
        ...raw,
        handle: clean(raw.handle),
        model: clean(raw.model) || clean(raw.name) || clean(raw.handle),
        facts: raw.facts && typeof raw.facts === 'object' ? raw.facts : {},
        variants: Array.isArray(raw.variants) ? raw.variants.filter(variant => variant && typeof variant === 'object' && clean(variant.id) && packs.has(variant.pack)) : []
      };
      if (byHandle.has(product.handle)) continue;
      products.push(product);
      byHandle.set(product.handle, product);
    }
    if (products.length < 2) return;
    const rowKeys = new Set();
    const rows = data.rows.filter(row => {
      if (!row || !clean(row.key) || rowKeys.has(row.key)) return false;
      rowKeys.add(row.key);
      return true;
    });
    const uid = `model-comparison-${rootIndex}`;
    const third = root.querySelector('[data-compare-third]');
    const add = root.querySelector('[data-compare-add]');
    const remove = root.querySelector('[data-compare-remove]');
    const packButtons = Array.from(root.querySelectorAll('[data-compare-pack]'));
    const differences = root.querySelector('[data-compare-differences]');
    const status = root.querySelector('[data-compare-status]');
    const returnLinks = Array.from(root.querySelectorAll('[data-compare-return]'));
    const share = root.querySelector('[data-compare-share]');
    let shareInput = root.querySelector('input[data-compare-share-url]');
    const selectedVariants = new Map();
    const chosenColors = new Map();
    const defaults = ['rechargeable-mesh-nebulizer', 'portable-mesh-nebulizer', ...products.map(product => product.handle)];
    const maxProducts = selects[2] && third ? Math.min(3, products.length) : 2;
    let state;
    let returnProduct = null;
    let returnVariant = null;
    let statusText = '';

    function variantLink(product, variant) {
      const url = safeLink(variant?.url) || safeLink(product.url);
      if (!url) return '';
      if (variant) url.searchParams.set('variant', variant.id);
      return url.href;
    }

    function readState() {
      const url = new URL(location.href);
      const contextModel = url.searchParams.get('return_model');
      const contextVariant = url.searchParams.get('return_variant');
      returnProduct = byHandle.get(contextModel) || null;
      returnVariant = returnProduct?.variants.find(variant => variant.id === contextVariant) || null;
      const selected = [];
      for (const handle of (url.searchParams.get('models') || '').split(',')) {
        if (byHandle.has(handle) && !selected.includes(handle) && selected.length < maxProducts) selected.push(handle);
      }
      for (const handle of defaults) {
        if (selected.length >= 2) break;
        if (byHandle.has(handle) && !selected.includes(handle)) selected.push(handle);
      }
      const queryPack = url.searchParams.get('pack');
      const pack = packs.has(queryPack) ? queryPack : returnVariant?.pack || 'single';
      selectedVariants.clear();
      chosenColors.clear();
      (url.searchParams.get('variants') || '').split(',').forEach((id, index) => {
        const product = byHandle.get(selected[index]);
        const variant = product?.variants.find(item => item.id === id && item.pack === pack);
        if (!variant) return;
        selectedVariants.set(product.handle, variant);
        chosenColors.set(product.handle, clean(variant.color));
      });
      return {
        models: selected,
        pack,
        differences: ['1', 'true'].includes(url.searchParams.get('differences'))
      };
    }

    function stateUrl() {
      const url = new URL(location.href);
      url.searchParams.set('models', state.models.join(','));
      url.searchParams.set('pack', state.pack);
      url.searchParams.set('variants', state.models.map(handle => {
        const variant = selectedVariants.get(handle);
        return variant?.pack === state.pack ? variant.id : '';
      }).join(','));
      if (state.differences) url.searchParams.set('differences', '1');
      else url.searchParams.delete('differences');
      return url;
    }

    function writeUrl(mode) {
      const url = stateUrl();
      if (url.href !== location.href) {
        try { history[mode === 'push' ? 'pushState' : 'replaceState'](history.state, '', url); } catch {}
      }
      if (shareInput) shareInput.value = url.href;
    }

    function chooseVariant(product) {
      const candidates = product.variants.filter(variant => variant.pack === state.pack);
      if (!candidates.length) return null;
      const original = returnProduct?.handle === product.handle ? returnVariant : null;
      const previous = selectedVariants.get(product.handle);
      const chosenColor = chosenColors.get(product.handle);
      const color = chosenColor || clean(original?.color) || clean(previous?.color);
      const variant = (chosenColor ? candidates.find(item => normalized(item.color) === normalized(chosenColor)) : null)
        || candidates.find(item => item.id === original?.id)
        || candidates.find(item => item.id === previous?.id)
        || (color ? candidates.find(item => normalized(item.color) === normalized(color)) : null)
        || candidates[0];
      selectedVariants.set(product.handle, variant);
      return variant;
    }

    function renderHeader(product, variant, index) {
      const cell = element('th');
      cell.scope = 'col';
      cell.id = `${uid}-product-${index}`;
      const card = element('div', null, 'compare-product');
      const src = safeImage(variant?.image) || safeImage(product.image);
      if (src) {
        const image = element('img', null, 'compare-product-image');
        image.src = src;
        image.alt = `${product.model}${variant?.color ? ` — ${variant.color}` : ''}`;
        image.width = 240;
        image.height = 240;
        image.loading = 'lazy';
        card.append(image);
      }
      card.append(element('strong', product.model, 'compare-product-model'));
      if (Array.isArray(product.cardSpecs)) {
        const specs = element('ul', null, 'compare-product-specs');
        product.cardSpecs.slice(0, 2).forEach(spec => specs.append(element('li', clean(spec))));
        card.append(specs);
      }
      card.append(element('p', variant ? clean(variant.priceText) || 'Price unavailable' : 'Package not offered', 'compare-product-price'));
      card.append(element('p', variant ? clean(variant.displayTitle || variant.title) || (state.pack === 'double' ? 'Two-device pack' : 'Single-device package') : `No ${state.pack === 'double' ? 'two-device pack' : 'single-device package'} listed.`, 'compare-product-option'));
      const colors = new Map();
      product.variants.filter(item => item.pack === state.pack).forEach(item => {
        if (clean(item.color) && !colors.has(normalized(item.color))) colors.set(normalized(item.color), item);
      });
      if (colors.size > 1 && variant) {
        const label = element('label', null, 'compare-color');
        label.append(element('span', 'Color'));
        const select = element('select');
        select.dataset.compareColor = product.handle;
        select.setAttribute('aria-label', `${product.model} color`);
        colors.forEach(item => {
          const option = element('option', item.color);
          option.value = item.id;
          select.append(option);
        });
        select.value = variant.id;
        select.addEventListener('change', () => {
          const choice = product.variants.find(item => item.id === select.value && item.pack === state.pack);
          if (!choice) return;
          chosenColors.set(product.handle, clean(choice.color));
          selectedVariants.set(product.handle, choice);
          commit();
          // The table is rebuilt; restore the same model's color control only.
          const replacement = Array.from(table.querySelectorAll('[data-compare-color]')).find(item => item.dataset.compareColor === product.handle);
          replacement?.focus({preventScroll:true});
        });
        label.append(select);
        card.append(label);
      }
      if (variant?.available === false) card.append(element('p', 'Currently unavailable', 'compare-product-availability'));
      const href = variantLink(product, variant);
      if (href) {
        const link = element('a', `View ${product.model} →`, 'compare-product-link');
        link.href = href;
        card.append(link);
      }
      cell.append(card);
      return cell;
    }

    function renderPackage(variant) {
      const container = element('div', null, 'compare-package');
      if (!variant) {
        container.append(element('p', 'This package is not listed for this model.', 'compare-fact-value'));
        return container;
      }
      const contents = strings(variant.contents);
      const exclusions = strings(variant.exclusions);
      if (contents.length) {
        const list = element('ul', null, 'compare-package-list');
        contents.forEach(item => list.append(element('li', item)));
        container.append(list);
      } else container.append(element('p', 'Package contents not specified.', 'compare-fact-value'));
      if (exclusions.length) {
        const block = element('div', null, 'compare-package-exclusions');
        block.append(element('p', 'Not included:'));
        const list = element('ul');
        exclusions.forEach(item => list.append(element('li', item)));
        block.append(list);
        container.append(block);
      }
      return container;
    }

    function render() {
      const selected = state.models.map(handle => byHandle.get(handle));
      table.dataset.modelCount = String(selected.length);
      const variants = selected.map(chooseVariant);
      selects.forEach((select, index) => {
        if (!select) return;
        select.value = state.models[index] || '';
        select.disabled = index >= state.models.length;
      });
      if (third) third.hidden = state.models.length < 3;
      if (add) {
        add.hidden = state.models.length >= maxProducts;
        add.disabled = state.models.length >= maxProducts;
      }
      if (remove) remove.hidden = state.models.length < 3;
      packButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.comparePack === state.pack)));
      if (differences) differences.checked = state.differences;

      const head = element('thead');
      const headRow = element('tr');
      const feature = element('th', 'Compare models');
      feature.scope = 'col';
      headRow.append(feature);
      selected.forEach((product, index) => headRow.append(renderHeader(product, variants[index], index)));
      head.append(headRow);
      const body = element('tbody');
      let hiddenCount = 0;
      let visibleCount = 0;
      rows.forEach((row, rowIndex) => {
        const values = selected.map(product => clean(product.facts[row.key]) || 'Not specified');
        const same = values.every(value => normalized(value) === normalized(values[0]));
        if (state.differences && same) { hiddenCount += 1; return; }
        visibleCount += 1;
        const tr = element('tr');
        const label = element('th', clean(row.label) || row.key);
        label.scope = 'row';
        label.id = `${uid}-fact-${rowIndex}`;
        tr.append(label);
        values.forEach((value, index) => {
          const cell = element('td');
          cell.setAttribute('headers', `${uid}-product-${index} ${label.id}`);
          cell.append(element('span', value, 'compare-fact-value'));
          tr.append(cell);
        });
        body.append(tr);
      });
      if (!visibleCount) {
        const tr = element('tr');
        const cell = element('td', state.differences ? 'No differences in the listed specifications. Package contents are shown below.' : 'Specifications are not listed for these models. Package contents are shown below.', 'compare-empty');
        cell.colSpan = selected.length + 1;
        tr.append(cell);
        body.append(tr);
      }
      // Contents always remain visible: model names and quantities can differ
      // even when specification values match, and unknown must not mean "No".
      const packages = element('tr');
      const packageLabel = element('th', 'Included in this package');
      packageLabel.scope = 'row';
      packageLabel.id = `${uid}-package`;
      packages.append(packageLabel);
      variants.forEach((variant, index) => {
        const cell = element('td');
        cell.setAttribute('headers', `${uid}-product-${index} ${packageLabel.id}`);
        cell.append(renderPackage(variant));
        packages.append(cell);
      });
      body.append(packages);
      const help = element('tr');
      const helpLabel = element('th', 'Model-specific help');
      helpLabel.scope = 'row';
      helpLabel.id = `${uid}-help`;
      help.append(helpLabel);
      selected.forEach((product, index) => {
        const cell = element('td');
        cell.setAttribute('headers', `${uid}-product-${index} ${helpLabel.id}`);
        const href = safeLink(product.supportUrl);
        if (href) {
          const link = element('a', `${product.model} help →`, 'compare-support-link');
          link.href = href.href;
          cell.append(link);
        } else cell.append(element('span', 'Model-specific help not listed.', 'compare-fact-value'));
        help.append(cell);
      });
      body.append(help);
      // Preserve any server-rendered caption or colgroup.
      table.querySelectorAll('thead, tbody, tfoot').forEach(section => section.remove());
      table.append(head, body);
      const returnHref = returnProduct && returnVariant ? variantLink(returnProduct, returnVariant) : '';
      returnLinks.forEach(link => {
        link.hidden = !returnHref;
        if (returnHref) {
          link.href = returnHref;
          link.textContent = `Return to ${returnProduct.model} — ${returnVariant.title}`;
        } else link.removeAttribute('href');
      });
      statusText = `Comparing ${selected.length} models · ${state.pack === 'double' ? 'Two-device packs' : 'Single-device packages'}.`;
      if (state.differences) statusText += ` ${hiddenCount} matching specification ${hiddenCount === 1 ? 'row hidden' : 'rows hidden'}. Package contents remain visible.`;
      if (status) status.textContent = statusText;
      if (shareInput && !shareInput.hidden) shareInput.value = stateUrl().href;
    }

    function commit() {
      render();
      writeUrl('push');
    }

    // Populate once. Later updates change only values and aria state, retaining
    // the focused select, button or checkbox throughout an interaction.
    selects.forEach((select, index) => {
      if (!select) return;
      select.replaceChildren(...products.map(product => {
        const option = element('option', product.model);
        option.value = product.handle;
        return option;
      }));
      if (!select.hasAttribute('aria-label') && !select.labels?.length) select.setAttribute('aria-label', `Product ${index + 1}`);
      select.addEventListener('change', () => {
        if (index >= state.models.length || !byHandle.has(select.value)) return;
        const previous = state.models[index];
        const duplicate = state.models.indexOf(select.value);
        if (duplicate !== -1 && duplicate !== index) state.models[duplicate] = previous;
        state.models[index] = select.value;
        commit();
      });
    });
    add?.addEventListener('click', () => {
      if (state.models.length >= maxProducts) return;
      const next = products.find(product => !state.models.includes(product.handle));
      if (!next) return;
      state.models.push(next.handle);
      commit();
      selects[2]?.focus({preventScroll:true});
    });
    remove?.addEventListener('click', () => {
      if (state.models.length < 3) return;
      state.models = state.models.slice(0, 2);
      commit();
      (add && !add.hidden ? add : selects[1]).focus({preventScroll:true});
    });
    packButtons.forEach(button => button.addEventListener('click', () => {
      if (!packs.has(button.dataset.comparePack)) return;
      state.pack = button.dataset.comparePack;
      commit();
    }));
    differences?.addEventListener('change', () => {
      state.differences = differences.checked;
      commit();
    });
    share?.addEventListener('click', async () => {
      const url = stateUrl().href;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(url);
        if (status) status.textContent = 'Comparison link copied.';
      } catch {
        if (!shareInput) {
          shareInput = element('input', null, 'compare-share-url');
          shareInput.type = 'text';
          shareInput.dataset.compareShareUrl = '';
          shareInput.setAttribute('aria-label', 'Comparison link to copy');
          share.insertAdjacentElement('afterend', shareInput);
        }
        shareInput.readOnly = true;
        shareInput.hidden = false;
        shareInput.value = url;
        shareInput.focus({preventScroll:true});
        shareInput.select();
        if (status) status.textContent = 'Copy the comparison link from the selected field.';
      }
    });
    window.addEventListener('popstate', () => {
      state = readState();
      render();
      if (shareInput) shareInput.value = stateUrl().href;
    });

    state = readState();
    render();
    writeUrl('replace');
    root.querySelectorAll('[data-compare-fallback]').forEach(fallback => { fallback.hidden = true; });
    root.querySelectorAll('[data-compare-controls]').forEach(controls => { controls.hidden = false; });
    root.dataset.comparisonInitialized = 'true';
    root.dataset.ready = 'true';
  });
})();
